import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ConfirmDialog, useConfirmDialog } from '@/components/ui/confirm-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Globe, Plus, ExternalLink, Trash2, Loader2, Search, BarChart3, RefreshCw } from 'lucide-react'
import { useGscConnections, useDisconnectGsc, useGscSites, type GscConnectionInfo } from '../hooks/useGsc'
import { useGa4Properties, type Ga4PropertyMatch } from '../hooks/useGa4'
import { useBingAccounts, useAddBingAccount, useRemoveBingAccount, type BingAccount } from '../hooks/useBing'
import { useDebounce } from '@/client/hooks/useDebounce'

/** Extract clean domain from any GSC property format */
function extractDomain(siteUrl: string): string {
  if (siteUrl.startsWith('sc-domain:')) return siteUrl.slice(10)
  try {
    const url = new URL(siteUrl)
    return url.hostname.replace(/^www\./, '')
  } catch {
    return siteUrl
  }
}

interface GroupedSite {
  domain: string
  properties: Array<{ siteUrl: string; type: 'domain' | 'url-prefix'; email: string; permissionLevel: string }>
  accounts: string[]
}

function groupSites(sites: Array<{ siteUrl: string; permissionLevel: string; email: string }>): GroupedSite[] {
  const map = new Map<string, GroupedSite>()

  for (const site of sites) {
    const domain = extractDomain(site.siteUrl)
    const type = site.siteUrl.startsWith('sc-domain:') ? 'domain' as const : 'url-prefix' as const

    let group = map.get(domain)
    if (!group) {
      group = { domain, properties: [], accounts: [] }
      map.set(domain, group)
    }

    group.properties.push({ siteUrl: site.siteUrl, type, email: site.email, permissionLevel: site.permissionLevel })
    if (!group.accounts.includes(site.email)) {
      group.accounts.push(site.email)
    }
  }

  return [...map.values()].sort((a, b) => a.domain.localeCompare(b.domain))
}

function GoogleTab() {
  const { data, isLoading } = useGscConnections()
  const disconnectGsc = useDisconnectGsc()
  const connections = data?.connections || []
  const canManage = data?.canManage ?? false
  const confirmDisconnect = useConfirmDialog<string>({
    onConfirm: async (id) => { await disconnectGsc.mutateAsync(id) },
  })

  // Check for ?gsc=connected in URL for success toast
  const [showConnected, setShowConnected] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('gsc') === 'connected') {
      window.history.replaceState({}, '', window.location.pathname)
      return true
    }
    return false
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Connect Google accounts to access Search Console and Analytics data for your sites.
        </p>
        <Button size="sm" variant="outline" asChild className="shrink-0">
          <a href="/api/gsc/connect">
            <Plus className="mr-2 h-4 w-4" />
            Connect Account
          </a>
        </Button>
      </div>

      {showConnected && (
        <div className="rounded-md border border-green-600/30 bg-green-50 dark:bg-green-950/20 p-3 text-sm text-green-800 dark:text-green-300 flex items-center justify-between">
          <span>Google account connected successfully.</span>
          <Button variant="ghost" size="sm" className="h-auto p-0 text-xs" onClick={() => setShowConnected(false)}>
            Dismiss
          </Button>
        </div>
      )}

      {isLoading ? (
        <Skeleton className="h-16 w-full" />
      ) : connections.length === 0 ? (
        <Card>
          <CardContent className="py-6 text-center">
            <Globe className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              No Google accounts connected yet.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {connections.map((conn: GscConnectionInfo) => (
            <Card key={conn.id}>
              <CardContent className="py-3 px-4 flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{conn.email}</p>
                    {conn.displayName && (
                      <p className="text-xs text-muted-foreground truncate">{conn.displayName}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {conn.hasGsc && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        <Search className="h-2.5 w-2.5 mr-0.5" />
                        GSC
                      </Badge>
                    )}
                    {conn.hasAnalytics ? (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        <BarChart3 className="h-2.5 w-2.5 mr-0.5" />
                        GA4
                      </Badge>
                    ) : (
                      <a href="/api/gsc/connect" className="text-[10px] text-primary hover:underline whitespace-nowrap">
                        Reconnect for Analytics
                      </a>
                    )}
                  </div>
                  <Badge variant="outline" className="text-xs shrink-0">
                    Connected {new Date(conn.createdAt).toLocaleDateString('en-AU')}
                  </Badge>
                </div>
                {canManage && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive shrink-0"
                    aria-label={`Disconnect ${conn.displayName || conn.email}`}
                    onClick={() => confirmDisconnect.openDialog(conn.id)}
                    disabled={disconnectGsc.isPending}
                  >
                    {disconnectGsc.isPending ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Trash2 className="h-3 w-3" />
                    )}
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {connections.length > 0 && <GscSitesBrowser />}
      {connections.length > 0 && connections.some((c) => c.hasAnalytics) && <Ga4PropertiesBrowser />}

      <ConfirmDialog
        open={confirmDisconnect.open}
        onOpenChange={confirmDisconnect.setOpen}
        title="Disconnect Google Account?"
        description="This will remove the connection and its Search Console and Analytics data access. You can reconnect at any time."
        confirmLabel="Disconnect"
        isLoading={confirmDisconnect.isLoading}
        onConfirm={confirmDisconnect.handleConfirm}
      />
    </div>
  )
}

function GscSitesBrowser() {
  const { data, isLoading } = useGscSites()
  const [search, setSearch] = useState('')
  const [filterAccount, setFilterAccount] = useState('__any__')
  const debouncedSearch = useDebounce(search, 200)
  const sites = data?.sites || []

  const accounts = [...new Set(sites.map((s) => s.email))].sort()
  const grouped = groupSites(sites)

  const filtered = grouped.filter((g) => {
    if (filterAccount !== '__any__' && !g.accounts.includes(filterAccount)) return false
    if (debouncedSearch) {
      return g.domain.toLowerCase().includes(debouncedSearch.toLowerCase())
    }
    return true
  })

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">
            GSC Sites
            <span className="text-muted-foreground font-normal ml-2 text-sm">
              {grouped.length} domains ({sites.length} properties)
            </span>
          </CardTitle>
          {filtered.length !== grouped.length && (
            <Badge variant="secondary" className="text-xs">{filtered.length} matching</Badge>
          )}
        </div>
        <div className="flex gap-2 pt-1">
          <Input
            placeholder="Search domains..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 text-sm"
          />
          {accounts.length > 1 && (
            <Select value={filterAccount} onValueChange={setFilterAccount}>
              <SelectTrigger className="h-8 w-48 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__any__">All accounts</SelectItem>
                {accounts.map((email) => (
                  <SelectItem key={email} value={email}>{email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            {debouncedSearch ? 'No domains match your search.' : 'No sites found.'}
          </p>
        ) : (
          <div className="max-h-96 overflow-y-auto space-y-0.5">
            {filtered.slice(0, 100).map((group) => (
              <div key={group.domain} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-accent/50 text-sm">
                <span className="font-mono text-xs min-w-0 flex-1 mr-2">{group.domain}</span>
                <div className="flex items-center gap-1.5 shrink-0">
                  {group.properties.some((p) => p.type === 'domain') && (
                    <Badge variant="default" className="text-[10px] px-1.5 py-0">Domain</Badge>
                  )}
                  {group.properties.some((p) => p.type === 'url-prefix') && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">URL</Badge>
                  )}
                  {group.accounts.length === 1 ? (
                    <span className="text-xs text-muted-foreground truncate max-w-[140px]" title={group.accounts[0]}>
                      {group.accounts[0]}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground" title={group.accounts.join(', ')}>
                      {group.accounts.length} accounts
                    </span>
                  )}
                </div>
              </div>
            ))}
            {filtered.length > 100 && (
              <p className="text-xs text-muted-foreground text-center pt-2">
                Showing 100 of {filtered.length} — refine your search to see more.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function Ga4PropertiesBrowser() {
  const { data, isLoading } = useGa4Properties()
  const resolving = data?.resolving
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 200)
  const properties = data?.properties || []

  // Group by domain
  const grouped = new Map<string, Ga4PropertyMatch[]>()
  for (const p of properties) {
    const key = p.domain || 'Unknown'
    const arr = grouped.get(key) || []
    arr.push(p)
    grouped.set(key, arr)
  }

  const domains = [...grouped.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .filter(([domain]) => {
      if (!debouncedSearch) return true
      return domain.toLowerCase().includes(debouncedSearch.toLowerCase())
    })

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">
            <BarChart3 className="inline h-4 w-4 mr-1.5" />
            GA4 Properties
            {resolving ? (
              <span className="text-muted-foreground font-normal ml-2 text-sm">
                <RefreshCw className="inline h-3 w-3 mr-1 animate-spin" />
                Discovering properties...
              </span>
            ) : (
              <span className="text-muted-foreground font-normal ml-2 text-sm">
                {properties.length} properties ({grouped.size} domains)
              </span>
            )}
          </CardTitle>
          {domains.length !== grouped.size && (
            <Badge variant="secondary" className="text-xs">{domains.length} matching</Badge>
          )}
        </div>
        <div className="pt-1">
          <Input
            placeholder="Search domains..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 text-sm"
          />
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : domains.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            {debouncedSearch ? 'No properties match your search.' : 'No GA4 properties found.'}
          </p>
        ) : (
          <div className="max-h-96 overflow-y-auto space-y-0.5">
            {domains.slice(0, 100).map(([domain, props]) => (
              <div key={domain} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-accent/50 text-sm">
                <span className="font-mono text-xs min-w-0 flex-1 mr-2">{domain}</span>
                <div className="flex items-center gap-1.5 shrink-0">
                  {props.map((p) => (
                    <Badge key={p.propertyId} variant="outline" className="text-[10px] px-1.5 py-0" title={`Property ${p.propertyId} (${p.email})`}>
                      {p.displayName}
                    </Badge>
                  ))}
                  {props.length === 1 ? (
                    <span className="text-xs text-muted-foreground truncate max-w-[140px]" title={props[0]?.email}>
                      {props[0]?.email}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      {[...new Set(props.map((p) => p.email))].length} accounts
                    </span>
                  )}
                </div>
              </div>
            ))}
            {domains.length > 100 && (
              <p className="text-xs text-muted-foreground text-center pt-2">
                Showing 100 of {domains.length} — refine your search to see more.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function BingTab() {
  const { data, isLoading } = useBingAccounts()
  const addAccount = useAddBingAccount()
  const removeAccount = useRemoveBingAccount()
  const [showAdd, setShowAdd] = useState(false)
  const [apiKey, setApiKey] = useState('')
  const [email, setEmail] = useState('')
  const [addError, setAddError] = useState('')
  const accounts = data?.accounts || []

  const confirmRemove = useConfirmDialog<string>({
    onConfirm: async (id) => { await removeAccount.mutateAsync(id) },
  })

  const handleAdd = async () => {
    setAddError('')
    try {
      await addAccount.mutateAsync({ apiKey: apiKey.trim(), email: email.trim() || undefined })
      setApiKey('')
      setEmail('')
      setShowAdd(false)
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to add account')
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Add Bing Webmaster API keys to auto-connect Bing data when new sites are created.
        </p>
        <Button size="sm" variant="outline" onClick={() => setShowAdd(!showAdd)} className="shrink-0">
          <Plus className="mr-2 h-4 w-4" />
          Add Account
        </Button>
      </div>

      {showAdd && (
        <Card>
          <CardContent className="py-4 space-y-3">
            <div>
              <label className="text-sm font-medium">API Key</label>
              <Input
                placeholder="Paste your Bing Webmaster API key"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Get your key from{' '}
                <a href="https://www.bing.com/webmasters/apikey" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Bing Webmaster Tools
                </a>
              </p>
            </div>
            <div>
              <label className="text-sm font-medium">Label (optional)</label>
              <Input
                placeholder="e.g. jezweb@outlook.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1"
              />
            </div>
            {addError && (
              <p className="text-sm text-destructive">{addError}</p>
            )}
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAdd} disabled={!apiKey.trim() || addAccount.isPending}>
                {addAccount.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Add Account'
                )}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setShowAdd(false); setAddError('') }}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <Skeleton className="h-16 w-full" />
      ) : accounts.length === 0 && !showAdd ? (
        <Card>
          <CardContent className="py-6 text-center">
            <Search className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              No Bing accounts connected yet.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {accounts.map((account: BingAccount) => (
            <Card key={account.id}>
              <CardContent className="py-3 px-4 flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{account.email || 'Bing Account'}</p>
                  </div>
                  <Badge variant="outline" className="text-xs shrink-0">
                    {account.site_count} sites
                  </Badge>
                  <Badge variant="outline" className="text-xs shrink-0">
                    Connected {new Date(account.created_at).toLocaleDateString('en-AU')}
                  </Badge>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive shrink-0"
                  aria-label={`Remove ${account.email || 'Bing account'}`}
                  onClick={() => confirmRemove.openDialog(account.id)}
                  disabled={removeAccount.isPending}
                >
                  {removeAccount.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Trash2 className="h-3 w-3" />
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={confirmRemove.open}
        onOpenChange={confirmRemove.setOpen}
        title="Remove Bing Account?"
        description="This will remove the account and disconnect all sites linked to it. Per-site Bing connections will be unaffected."
        confirmLabel="Remove"
        isLoading={confirmRemove.isLoading}
        onConfirm={confirmRemove.handleConfirm}
      />
    </div>
  )
}

export function IntegrationsPage() {
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Accounts</h1>
        <p className="text-sm text-muted-foreground">Manage connected accounts for search data integrations</p>
      </div>

      <Tabs defaultValue="google">
        <TabsList>
          <TabsTrigger value="google">
            <Globe className="h-4 w-4 mr-1.5" />
            Google
          </TabsTrigger>
          <TabsTrigger value="bing">
            <Search className="h-4 w-4 mr-1.5" />
            Bing
          </TabsTrigger>
        </TabsList>
        <TabsContent value="google">
          <GoogleTab />
        </TabsContent>
        <TabsContent value="bing">
          <BingTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}
